import { SalesProductSummary } from './salesProductsApi';
import { apiClient } from './apiClient';

export type ProspectPersonaSource = 'Manual' | 'Inferred';

export type ProspectPersonaLink = {
  buyerPersonaId: string;
  name: string;
  jobTitle: string;
  seniority: string;
  confidenceScore: number;
  source: ProspectPersonaSource;
};

export type ProspectPersonaLinkItemPayload = {
  buyerPersonaId: string;
  confidenceScore?: number;
  source?: ProspectPersonaSource;
};

export type ProspectActionPermissions = {
  canEdit: boolean;
  canSoftDelete: boolean;
  canMarkLost: boolean;
  canPermanentDelete: boolean;
};

export type Prospect = {
  id: string;
  fullName: string;
  title?: string;
  companyName?: string;
  companyId?: string;
  companyDomain?: string;
  location?: string;
  cityId?: string;
  stateId?: string;
  countryId?: string;
  linkedinUrl?: string;
  companyLinkedinUrl?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  emailValidationStatus: string;
  emailConfidenceScore?: number;
  captureSource?: string;
  campaignTag?: string;
  enrichmentStatus: string;
  funnelStage: string;
  actions: ProspectActionPermissions;
  collectedAtUtc: string;
  isOptedOut: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  products: SalesProductSummary[];
  buyerPersonas: ProspectPersonaLink[];
};

export type ProspectListResult = {
  items: Prospect[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type ProspectListFilter = {
  companyName?: string;
  companyId?: string;
  title?: string;
  enrichmentStatus?: string;
  hasEmail?: boolean;
  captureSource?: string;
  campaignTag?: string;
  funnelStage?: string;
  collectedFromUtc?: string;
  collectedToUtc?: string;
  page?: number;
  pageSize?: number;
};

export type ProspectUpsertPayload = {
  fullName: string;
  title?: string;
  companyName?: string;
  companyId?: string;
  companyDomain?: string;
  location?: string;
  cityId?: string;
  stateId?: string;
  countryId?: string;
  linkedinUrl?: string;
  companyLinkedinUrl?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  captureSource?: string;
  campaignTag?: string;
  productIds?: string[];
  personaLinks?: ProspectPersonaLinkItemPayload[];
  rawText?: string;
  parserVersion?: string;
  collectedAtUtc?: string;
};

export async function listProspects(filter: ProspectListFilter = {}): Promise<ProspectListResult> {
  const response = await apiClient.get<ProspectListResult>('/v1/prospects', { params: filter });
  return response.data;
}

export async function getProspect(id: string): Promise<Prospect> {
  const response = await apiClient.get<Prospect>(`/v1/prospects/${id}`);
  return response.data;
}

export async function createProspect(payload: ProspectUpsertPayload): Promise<Prospect> {
  const response = await apiClient.post<Prospect>('/v1/prospects', payload);
  return response.data;
}

export async function updateProspect(id: string, payload: ProspectUpsertPayload): Promise<Prospect> {
  const response = await apiClient.put<Prospect>(`/v1/prospects/${id}`, payload);
  return response.data;
}

export async function deleteProspect(id: string): Promise<void> {
  await apiClient.delete(`/v1/prospects/${id}`);
}

export async function markProspectLost(id: string): Promise<void> {
  await apiClient.post(`/v1/prospects/${id}/mark-lost`);
}

export async function permanentDeleteProspect(id: string): Promise<void> {
  await apiClient.delete(`/v1/prospects/${id}/permanent`);
}

export async function enrichProspect(id: string): Promise<void> {
  await apiClient.post(`/v1/prospects/${id}/enrich`);
}

export async function enrichProspectsBatch(prospectIds: string[]): Promise<{ queuedCount: number }> {
  const response = await apiClient.post<{ queuedCount: number }>('/v1/prospects/enrich/batch', {
    prospectIds,
  });
  return response.data;
}

export async function getProspectProducts(id: string): Promise<SalesProductSummary[]> {
  const response = await apiClient.get<SalesProductSummary[]>(`/v1/prospects/${id}/products`);
  return response.data;
}

export async function replaceProspectProducts(id: string, productIds: string[]): Promise<void> {
  await apiClient.put(`/v1/prospects/${id}/products`, { productIds });
}

export async function getProspectBuyerPersonas(id: string): Promise<ProspectPersonaLink[]> {
  const response = await apiClient.get<ProspectPersonaLink[]>(`/v1/prospects/${id}/buyer-personas`);
  return response.data;
}

export async function replaceProspectBuyerPersonas(
  id: string,
  items: ProspectPersonaLinkItemPayload[],
): Promise<void> {
  await apiClient.put(`/v1/prospects/${id}/buyer-personas`, { items });
}
